"use client";

import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { Icon, Text, View } from "@/components";
import { useLayout } from "@/providers";

export interface AttemptTimerProps {
  /**
   * Vencimiento absoluto en milisegundos (epoch). Derivado del `expira_en` /
   * `tiempo_restante_seg` del servidor: el temporizador es un espejo, el árbitro
   * del tiempo es siempre el backend.
   */
  deadlineMs: number | null;
  /** Se invoca UNA vez cuando el tiempo llega a cero (la página fuerza el envío). */
  onExpired: () => void;
}

const formatRemaining = (totalSeg: number): string => {
  const min = Math.floor(totalSeg / 60);
  const seg = totalSeg % 60;
  return `${String(min).padStart(2, "0")}:${String(seg).padStart(2, "0")}`;
};

/**
 * @name AttemptTimer
 * @description Temporizador del intento (A6). Renderiza el tiempo restante derivado
 * del vencimiento del servidor y dispara `onExpired` al agotarse. Ante desfase de
 * reloj manda el servidor (que rechaza respuestas tardías de todos modos).
 * Anuncia el tiempo restante de forma no intrusiva (aria-live).
 *
 * @param {AttemptTimerProps} props - Vencimiento absoluto y callback de expiración.
 * @returns {JSX.Element} El temporizador renderizado (vacío si no hay tiempo límite).
 */
const AttemptTimer = ({
  deadlineMs,
  onExpired
}: AttemptTimerProps): JSX.Element | null => {
  const { t } = useLayout();
  const [remaining, setRemaining] = useState<number | null>(null);
  const expiredRef = useRef(false);

  useEffect(() => {
    if (!deadlineMs) return;
    expiredRef.current = false;

    const tick = () => {
      const seg = Math.max(0, Math.floor((deadlineMs - Date.now()) / 1000));
      setRemaining(seg);
      if (seg <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpired();
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [deadlineMs, onExpired]);

  if (!deadlineMs || remaining === null) return null;

  const critico = remaining <= 60;

  return (
    <View
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
        critico
          ? "bg-destructive/10 text-destructive"
          : "bg-primary/10 text-primary"
      }`}
      aria-live="polite"
      role="timer"
    >
      <Icon name="fa-regular fa-clock" />
      <Text type="span">{`${t("timeRemaining")}: ${formatRemaining(remaining)}`}</Text>
    </View>
  );
};

export default AttemptTimer;
